import React, {useEffect, useRef, useState} from "react";
import "./assets/scss/Dashboard.scss";
import LoadingAnimation from "../components/LoadingAnimation";

import Meter from "./Meter";

import CreateMeterModal from "./modals/CreateMeterModal";
import { DashboardSettingsModal } from "./modals/DashboardSettingsModal";

import {
    getPdash,
    savePdashMeter,
    prosLogErr,
    prosLog,
    METER_TEMPLATES,
    getActiveProject,
    getPdashProjectSettings,
    getCurrentProjectList,
    OBJ_STATE_ACT,
    OBJ_STATE_PAS,
    ATTR_AWS_PROJECT_ID, ATTR_OBJ_ID, getMainProject, getValidInteger, ATTR_CREATED_MS_I, isSuperUserModeInUse,
} from "../prodect/ProsConnect";
import DashboardSubprojectInfo from "./DashboardSubprojectInfo";
import BasicButton from "../components/BasicButton";
import {DashboardMainPrjAddModal} from "./modals/DashboardMainPrjAddModal";
import prosAppNotif from "../prodect/ProsAppNotif";
import {addAText} from "../components/Common";
import {formatDate} from "../prodect/ProsCommonUi";
import DashboardMainProjectInfo from "./DashboardMainProjectInfo";

/**
 * @typedef {{ meters: Meter[] }} Dashboard
 * @typedef {{ obj_id: string, obj_type: `meter_${string}`, created_ms: number }} Meter
 * @typedef {{ obj_id: 'dashboard', obj_type: 'dashboard', order: string[], config: Record<string, MeterConfig> }} ViewPreferences
 * @typedef {{ size: 0 | 1 | 2 }} MeterConfig
 */

export const meterTypeComponentMapping = {
    meter_status: () => import('./meters/StatusMeter'),
    meter_cumulative: () => import('./meters/CumulativeMeter'),
    meter_readiness: () => import('./meters/ReadinessMeter'),
    meter_gantt: () => import('./meters/GanttMeter'),
}

const DASHBOARD_SETTINGS_OBJ_ID = 'dashboard'

export const pdashColors = {
    'color-bg-content':         '#ffffff',
    'color-main':               '#0068e2',
    'color-2nd':                '#d94709',
    'color-gray':               '#111418',
    'color-gray-60':            '#5f7086',
    'color-gray-40':            '#919fb1',
    'color-gray-20':            '#c8cfd8',
    'color-gray-5':             '#f1f3f5',
    'pdash-color-1':            '#2E5273',
    'pdash-color-2':            '#86519F',
    'pdash-color-3':            '#BC5090',
    'pdash-color-4':            '#FF6361',
    'pdash-color-5':            '#07D1DE',
    'pdash-color-6':            '#0EBA7C',
    'pdash-color-7':            '#448286',
    'pdash-color-state-maybe':  '#FBA100',
    'pdash-color-state-ok':     '#00d26d',
    'pdash-color-state-no':     '#ff5656',
    'pdash-color-state-main':   '#1c84ff',
    'pdash-color-state-info':   '#5f7086',
    'pdash-color-state-other':  '#919fb1',
    'pdash-color-state-others': '#c8cfd8',
}

export const pdashVars = {
    'text-size-sm': '13px',
}
/**
 * @type {ViewPreferences}
 */
const EMPTY_VIEW_PREFERENCES = {
    obj_id: DASHBOARD_SETTINGS_OBJ_ID,
    obj_type: DASHBOARD_SETTINGS_OBJ_ID,
    order: [],
    config: {},
}

const VIEW_PREFERENCES_DEBOUNCE_TIMEOUT = 2_000

export default function Dashboard(props) {
    const projectId = props.projectId
    const mainPrjPage = props?.mainPrj ?? false
    const [mainProject, setMainProject] = useState({})
    const [dashboard, setDashboard] = useState()
    const [viewPreferences, setViewPreferences] = useState(EMPTY_VIEW_PREFERENCES)
    const [viewPreferencesUpdated, setViewPreferencesUpdated] = useState(false)
    const [isUpdating, setIsUpdating] = useState(true)
    const [isCreateMeterDialogOpen, setIsCreateMeterDialogOpen] = useState(false)
    const [isAddMeterDialogOpen, setIsAddMeterDialogOpen] = useState(false)
    const [isCommonSettingsDialogOpen, setIsCommonSettingsDialogOpen] = useState(false)
    const [project_settings, setProject_settings] = useState()
    const [all_meters, setAll_meters] = useState([])

    const viewPrefsDebounceRef = useRef()

    useEffect(() => {
        updateDashboard()
        // Note: Do NOT add `updateDashboard` to dependency array or infinite loop will occur!
        // This is needed to avoid unnecessary build warning:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    useEffect(() => {
        if (viewPreferencesUpdated) {
            viewPrefsDebounceRef.current = setTimeout(() => {
                savePdashMeter(projectId, viewPreferences)
            }, VIEW_PREFERENCES_DEBOUNCE_TIMEOUT)
        }
        
        return () => {
            if (viewPrefsDebounceRef.current) {
                clearTimeout(viewPrefsDebounceRef.current)
            }
        }
    }, [projectId, viewPreferences, viewPreferencesUpdated])

    async function createDefaultMeters() {
        setIsCreateMeterDialogOpen(false)
        let defaultMetersInOrder = [METER_TEMPLATES[0]]
        defaultMetersInOrder[0].size = 1
        defaultMetersInOrder[0].selectedTag = 's' + getActiveProject().getStateRbTag()
        await savePdashMeter(projectId, defaultMetersInOrder)
        await updateDashboard()
    }

    async function updateDashboard() {
        setIsUpdating(true)
        if (!projectId) {
            return
        }
        if (mainPrjPage) {
            const mp = await getMainProject(projectId)
            setMainProject(mp)
        }
        const dashboard = await getPdash(projectId, mainPrjPage)
        prosLog('getPdash() projectId:', projectId)
        if ('errorType' in dashboard) {
            // Error in server interface
            prosLogErr("Failed to get dashboard", dashboard)
        }
        if (mainPrjPage && dashboard.hasOwnProperty('main_project') && dashboard.main_project === false) {
            prosLogErr('No main project found')
            prosAppNotif('Projektia ei löydetty')
        }
        if (dashboard && dashboard.meters) {
            if (dashboard.meters.length < 1) {
                if (mainPrjPage) {
                    if (dashboard.hasOwnProperty('main_project') && dashboard.main_project === false) {
                        prosLogErr('No main project found')
                        prosAppNotif('Projektia ei löydetty')
                        return
                    }
                    await savePdashMeter(projectId, {
                        obj_id: 'dashboard',
                        obj_type: 'dashboard',
                        order: [],
                        config: {},
                    })
                    updateDashboard()
                } else {
                    await createDefaultMeters()
                }
                return
            }
            const viewPreferences = getViewPreferencesObject(dashboard)
            setViewPreferences(viewPreferences)
            setDashboard(dashboard)
        }
        setIsUpdating(false)
    }

    function setMeterSize(meterId, size) {
        setViewPreferences({
            ...viewPreferences,
            config: {
                ...viewPreferences.config,
                [meterId]: {
                    ...(viewPreferences.config[meterId] ?? {}),
                    size,
                },
            },
        })
        setViewPreferencesUpdated(true)
    }

    function renderContent(sortedMeters){
        if(dashboard?.meters.length <= 1) return null
        return sortedMeters.map(meter => {
            return <Meter
                key={meter.obj_id}
                projectId={meter.project_id.split(';')[1]}
                meterSettings={meter}
                onUpdateRequested={() => updateDashboard()}
                editTableLinkSettings={mainPrjPage ? null : props.editTableLinkSettings}
                meterSize={viewPreferences.config[meter.obj_id]?.size ?? 0}
                onSizeChange={newSize => setMeterSize(meter.obj_id, newSize)}
            />
        })
    }

    function renderMeters(subprojects, inactive_subsprojects) {
        const sortedMeters = viewPreferences.order
            .map(id => dashboard.meters.find(meter => meter.obj_id === id))
            .filter(meter => meter)
        const uiBoxes = [
            {
                text: addAText(subprojects.length, 'osaprojekti', 'a'),
                detail: inactive_subsprojects.length > 0 ? inactive_subsprojects.length + ' arkistoitua' : null,
                img: "folder"
            },
            //Todo Jere
            // {
            //     text: addAText(1000,'käyttäjä','ä'),
            //     detail:  500 + ' aktiivista',//ei ole pakko jos menee hankalaksi
            //     img: "user"
            // },
        ]
        return <div id="DashboardSubproject" className="grid-container">
            {mainPrjPage
                ? isSuperUserModeInUse() ? <DashboardMainProjectInfo
                        projectId={props.projectId}
                        meterCount={sortedMeters.length}
                        openCommonSettings={() => setIsCommonSettingsDialogOpen(true)}
                        openAddChart={async () => {
                            let all_meters = await getPdash(projectId, true)
                            if (all_meters.hasOwnProperty('all_meters')) {
                                setAll_meters(all_meters.all_meters)
                            }
                            setIsAddMeterDialogOpen(true)
                        }}
                        mainProject={mainProject}
                    />
                        :
                    <div id="dashboard-info" className="block size-1">
                        <div className={"grid-container grid-" + (uiBoxes.length > 1 ? 2 : 1)}>
                            {uiBoxes.map((item, i)=> renderCard(item, i))}
                        </div>
                        <div className="card margin-top text-box">
                            {mainProject.hasOwnProperty(ATTR_CREATED_MS_I) ?
                                <p>Projekti on luotu {formatDate(getValidInteger(mainProject[ATTR_CREATED_MS_I], 0, 0))}</p> : ""}
                            <div className={"grid-container grid-1 grid-" + (sortedMeters.length > 1 ? 2 : 1) +  "_md margin-top"}>
                                <BasicButton
                                    btnClass={"primary"}
                                    label={"Lisää kuvaaja"}
                                    icon={"plus"}
                                    action={async () => {
                                        let all_meters = await getPdash(projectId, true)
                                        if (all_meters.hasOwnProperty('all_meters')) {
                                            setAll_meters(all_meters.all_meters)
                                        }
                                        setIsAddMeterDialogOpen(true)
                                    }}
                                    hilite={false}
                                />
                                {sortedMeters.length > 1 ? <BasicButton
                                        btnClass={"primary"}
                                        label={"Järjestele"}
                                        icon={"reorder"}
                                        action={() => setIsCommonSettingsDialogOpen(true)}
                                    /> : null}
                            </div>
                        </div>
                    </div>
                : <DashboardSubprojectInfo
                    projectId={props.projectId}
                    meterCount={sortedMeters.length}
                    openCommonSettings={() => setIsCommonSettingsDialogOpen(true)}
                    openAddChart={async () => {
                        const project_settings = await getPdashProjectSettings(projectId)
                        setProject_settings(project_settings)
                        setIsCreateMeterDialogOpen(true)
                    }}
            />}
            {renderContent(sortedMeters)}
        </div>
    }

    // Main render
    if (isUpdating) {
        return <LoadingAnimation size="lg" />
    }
    const allsubprojects = getCurrentProjectList()
    let subprojects = allsubprojects.filter(prj => prj.main_proj_id === projectId && prj.project_state === OBJ_STATE_ACT)
    const inactive_subsprojects = allsubprojects.filter(prj => prj.main_proj_id === projectId && prj.project_state === OBJ_STATE_PAS)
    return <>
        {isCreateMeterDialogOpen && (
            <CreateMeterModal
                project_settings={project_settings}
                projectId={projectId}
                dashboard={dashboard}
                onClose={() => {
                    setIsCreateMeterDialogOpen(false)
                    updateDashboard()
                }}
            />
        )}
        {isAddMeterDialogOpen && (
            <DashboardMainPrjAddModal
                all_meters={all_meters}
                subprojects={subprojects}
                viewPreferences={viewPreferences}
                onClose={selectedMeters => {
                    let dash = dashboard
                    let viewPref = {
                        ...viewPreferences,
                        order: [
                            ...viewPreferences.order.filter(meter_id => selectedMeters.find(meter => meter.obj_id === meter_id)),
                            ...selectedMeters.filter(meter => viewPreferences.order.indexOf(meter.obj_id) < 0).map(meter => meter.obj_id)
                        ],
                        meters: selectedMeters.map(meter => [meter[ATTR_AWS_PROJECT_ID], meter[ATTR_OBJ_ID]])
                    }
                    dash['meters'] = [viewPref, ...selectedMeters]
                    setDashboard(dash)
                    setViewPreferences(viewPref)
                    setViewPreferencesUpdated(true)
                    setIsAddMeterDialogOpen(false)
                }}
            />
        )}
        {isCommonSettingsDialogOpen && (
            <DashboardSettingsModal
                dashboard={dashboard}
                viewPreferences={viewPreferences}
                onClose={viewPreferences => {
                    setViewPreferences(viewPreferences)
                    setViewPreferencesUpdated(true)
                    setIsCommonSettingsDialogOpen(false)
                }}
            />
        )}
        {renderMeters(subprojects, inactive_subsprojects)}
    </>
}

/**
 * @param {Dashboard} dashboard
 * @returns {ViewPreferences}
 */
export function getViewPreferencesObject(dashboard) {
    // View preferences is a "meter settings" with id = DASHBOARD_SETTINGS_OBJ_ID
    let viewPreferences = dashboard.meters.find(meter => meter.obj_id === DASHBOARD_SETTINGS_OBJ_ID)
    if (!viewPreferences) {
        viewPreferences = EMPTY_VIEW_PREFERENCES
    }

    // Tolerate defects in "order" list by adding missing meters
    let order = [].concat(viewPreferences.order)
    dashboard.meters
        .filter(meter => meter.obj_id !== DASHBOARD_SETTINGS_OBJ_ID)
        .forEach(meter => {
            if (!order.includes(meter.obj_id)) {
                order.push(meter.obj_id)
            }
    })
    // Tolerate defects in "order" list by removing non existing meters
    const removeNonExistingMeters = order.filter(id => !dashboard.meters.find(meter => meter.obj_id === id))
    const newOrder = order.filter( id => !removeNonExistingMeters.includes(id))
    viewPreferences.order = newOrder
    return viewPreferences
}

/**
 * @param {string} meterType 
 */
export function getMeterIcon(meterType) {
    let icon = require(`./assets/images/dashboard/${meterType.replace('meter_', 'pdash-chart-')}.svg`)
    if (!icon) {
        prosLogErr("Missing icon for meter type", meterType)
        return "missing.svg"
    }
    return icon
}

export function renderCard(item, i){
    return <div key={i} className={"card"}>
        <figure>
            <i className={"ui-icon ui-icon-xl ui-icon_color-light ui-icon-" + item.img}/>
        </figure>
        <p className={"text-center margin-0"}>
            {item.text}
        </p>
        {item.detail ? <p className={"text-center margin-0 text-sm"}>({item.detail})</p> : null}
    </div>
}